/**
 * OrderCard Component
 * Individual order card for mobile view
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-007
 */

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusBadge } from './StatusBadge';
import { Calendar, Package, Truck, Eye } from 'lucide-react';
import type { CustomerOrderSummary } from '../types/order.types';

// ============================================
// COMPONENT PROPS
// ============================================

export interface OrderCardProps {
  order: CustomerOrderSummary;
  onViewDetails: (orderId: number) => void;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Format date to readable format
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Format currency
 */
function formatCurrency(amount: number): string {
  return `₹${amount.toLocaleString('en-IN')}`;
}

// ============================================
// COMPONENT
// ============================================

/**
 * OrderCard Component
 * Mobile-friendly card displaying order summary
 *
 * @example
 * ```tsx
 * <OrderCard
 *   order={orderData}
 *   onViewDetails={(id) => navigate(`/orders/${id}`)}
 * />
 * ```
 */
export function OrderCard({ order, onViewDetails }: OrderCardProps) {
  return (
    <Card className="hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-4 space-y-3">
        {/* Header: Order Number and Status */}
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-semibold text-base text-slate-900">
              {order.orderNumber}
            </h3>
            <p className="text-sm text-slate-500 mt-0.5">
              {formatDate(order.orderDate)}
            </p>
          </div>
          <StatusBadge status={order.orderStatus} size="sm" />
        </div>

        {/* Order Details */}
        <div className="space-y-2">
          {/* Amount */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Amount</span>
            <span className="text-base font-semibold text-slate-900">
              {formatCurrency(order.totalAmount)}
            </span>
          </div>

          {/* Quantity */}
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Package className="h-4 w-4" />
            <span>
              {order.quantity} {order.quantityUnit}
            </span>
          </div>

          {/* Expected Delivery Date */}
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Calendar className="h-4 w-4" />
            <span>
              Expected: {formatDate(order.expectedDeliveryDate)}
            </span>
          </div>

          {/* Actual Delivery Date (if available) */}
          {order.actualDeliveryDate && (
            <div className="flex items-center gap-2 text-sm text-emerald-600">
              <Truck className="h-4 w-4" />
              <span>
                Delivered: {formatDate(order.actualDeliveryDate)}
              </span>
            </div>
          )}
        </div>

        {/* Action Button */}
        <Button
          variant="outline"
          size="sm"
          className="w-full mt-2"
          onClick={() => onViewDetails(order.id)}
        >
          <Eye className="h-4 w-4 mr-2" />
          View Details
        </Button>
      </CardContent>
    </Card>
  );
}

export default OrderCard;
